import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Apple, CheckCircle, Clock, Download, Link2, Smartphone, Zap, ExternalLink, Copy, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const AppleWatchWizard = ({ user, isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [syncInstructions, setSyncInstructions] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
  const [lastSync, setLastSync] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSyncInstructions();
      checkConnectionStatus();
    }
  }, [isOpen]);

  const loadSyncInstructions = async () => {
    try {
      const response = await axios.get(`${API}/health-data/sync-instructions`);
      setSyncInstructions(response.data);
      
      // Generate QR code URL for easy mobile setup
      const webhookUrl = response.data.webhook_config.url;
      const qrData = encodeURIComponent(JSON.stringify({
        url: webhookUrl,
        email: user.email
      }));
      setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${qrData}`);
    } catch (error) {
      console.error('Error loading sync instructions:', error);
    }
  };

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/health-data`);
      if (response.data && response.data.length > 0) {
        setConnectionStatus('connected');
        // Get the most recent sync timestamp
        const latest = response.data.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        )[0];
        setLastSync(latest.timestamp);
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
  };

  const testConnection = async () => {
    setConnectionStatus('connecting');
    toast.info('Testing connection... Please wait');
    
    // Simulate connection test
    setTimeout(async () => {
      await checkConnectionStatus();
      if (connectionStatus === 'connected') {
        toast.success('✅ Apple Watch connected successfully!');
      } else {
        toast.warning('No data received yet. Please complete setup in the app.');
      }
    }, 2000);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const steps = [
    {
      title: 'Welcome',
      icon: Apple,
      component: (
        <div className="text-center space-y-6 py-8">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
            <Apple className="w-12 h-12 text-indigo-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800">Connect Your Apple Watch</h2>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            Seamlessly sync your health data from Apple Watch to HealthAI in just 3 simple steps
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-3">
                <Download className="w-8 h-8 text-teal-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">Download App</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-cyan-100 flex items-center justify-center mb-3">
                <Link2 className="w-8 h-8 text-cyan-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">Connect</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">Sync Data</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Download App',
      icon: Download,
      component: syncInstructions && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Step 1: Get the App</h3>
            <p className="text-gray-600 mb-6">
              Download the Health Auto Export app from the App Store on your iPhone
            </p>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-2xl border-2 border-indigo-200 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white shadow-lg flex items-center justify-center">
              <Smartphone className="w-10 h-10 text-indigo-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-800 mb-2">Health Auto Export</h4>
            <p className="text-gray-600 mb-6">JSON + CSV</p>
            
            {qrCodeUrl && (
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">Scan with your iPhone camera:</p>
                <div className="inline-block p-4 bg-white rounded-xl shadow-lg">
                  <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                </div>
              </div>
            )}

            <a
              href={syncInstructions.app_store_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all hover:scale-105"
            >
              <Download className="w-5 h-5" />
              Open App Store
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>💡 Note:</strong> The app requires a one-time purchase ($4.99) or subscription to enable automatic sync features.
            </p>
          </div>
        </div>
      )
    },
    {
      title: 'Configure',
      icon: Link2,
      component: syncInstructions && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Step 2: Configure Connection</h3>
            <p className="text-gray-600">
              Copy these settings into the Health Auto Export app
            </p>
          </div>

          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-bold text-gray-700">Webhook URL</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(syncInstructions.webhook_config.url)}
                  className="flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 p-3 rounded border border-gray-300 font-mono text-xs break-all">
                {syncInstructions.webhook_config.url}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-bold text-gray-700">Your Email</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(user.email)}
                  className="flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </Button>
              </div>
              <div className="bg-gray-50 p-3 rounded border border-gray-300 font-mono text-sm">
                {user.email}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <Label className="text-sm font-bold text-gray-700 block mb-2">API Key</Label>
              <div className="bg-gray-50 p-3 rounded border border-gray-300 font-mono text-sm">
                your-password (use your account password for demo)
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-lg border-2 border-teal-200">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              Quick Setup Guide
            </h4>
            <ol className="space-y-2 text-sm text-gray-700">
              <li>1. Open Health Auto Export app</li>
              <li>2. Tap "Automations" → "+" button</li>
              <li>3. Select "REST API" type</li>
              <li>4. Paste the Webhook URL above</li>
              <li>5. Add parameters: user_email and api_key</li>
              <li>6. Select metrics: Heart Rate, Steps, Sleep</li>
              <li>7. Set schedule: Every 1 hour</li>
              <li>8. Test with manual export</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      title: 'Complete',
      icon: CheckCircle,
      component: (
        <div className="text-center space-y-6 py-8">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center animate-pulse">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800">You're All Set!</h2>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            Your Apple Watch will now automatically sync health data to HealthAI
          </p>

          {connectionStatus === 'connected' && lastSync && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Zap className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-bold text-green-800">Connected Successfully!</h3>
              </div>
              <p className="text-sm text-green-700">
                Last sync: {new Date(lastSync).toLocaleString()}
              </p>
            </div>
          )}

          {connectionStatus !== 'connected' && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Clock className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-bold text-blue-800">Waiting for First Sync</h3>
              </div>
              <p className="text-sm text-blue-700 mb-4">
                Data will appear here once you complete a manual export from the app
              </p>
              <Button
                onClick={testConnection}
                disabled={connectionStatus === 'connecting'}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                {connectionStatus === 'connecting' ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Test Connection
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-200 max-w-md mx-auto">
            <h4 className="font-bold text-gray-800 mb-3">What's Next?</h4>
            <ul className="space-y-2 text-sm text-gray-700 text-left">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Heart rate, steps, and sleep data will sync every hour</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>View your health metrics on the Health Data page</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Your data stays private and secure</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Historical data from Health app will be imported</span>
              </li>
            </ul>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const closeWizard = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeWizard}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <div className="sticky top-0 bg-white z-10 border-b">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Apple className="w-6 h-6 text-white" />
              </div>
              Apple Watch Connection
            </DialogTitle>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="px-6 pb-4">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;
                
                return (
                  <div key={`item-${index}`} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                          isActive
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg scale-110'
                            : isCompleted
                            ? 'bg-green-500'
                            : 'bg-gray-200'
                        }`}
                      >
                        <StepIcon className={`w-6 h-6 ${isActive || isCompleted ? 'text-white' : 'text-gray-400'}`} />
                      </div>
                      <span
                        className={`text-xs mt-2 font-medium ${
                          isActive ? 'text-indigo-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`h-1 flex-1 mx-2 rounded transition-all ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="p-8">
          {steps[currentStep].component}
        </div>

        {/* Navigation */}
        <div className="sticky bottom-0 bg-gray-50 border-t p-6 flex justify-between">
          <Button
            onClick={prevStep}
            disabled={currentStep === 0}
            variant="outline"
            className="px-8"
          >
            Previous
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button
              onClick={nextStep}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8"
            >
              Next Step
            </Button>
          ) : (
            <Button
              onClick={closeWizard}
              className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-8"
            >
              Done
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppleWatchWizard;
